/**
 * Shared 24-hour time grid used by DayView and WeekView.
 *
 * Renders hour labels on the left, horizontal gridlines, optional column
 * headers, and provides relative-positioned columns for absolute event blocks.
 *
 * @module components/calendar/TimeGrid
 */

import { useEffect, useRef } from 'react'
import { format } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import { isSameDayTz } from '../../utils/calendarLayout'
import NowIndicator from './NowIndicator'

const HOURS = Array.from({ length: 24 }, (_, i) => i)

function formatHourLabel(hour) {
  if (hour === 0) return '12 AM'
  if (hour < 12) return `${hour} AM`
  if (hour === 12) return '12 PM'
  return `${hour - 12} PM`
}

/**
 * @param {{
 *   columnCount: number,
 *   columnHeaders?: Array<{ label: string, sublabel: string, date: Date }>,
 *   hourHeight?: number,
 *   userTz: string,
 *   renderColumn: (columnIndex: number) => React.ReactNode,
 *   showNowLine?: boolean,
 *   currentDate?: Date
 * }} props
 */
export default function TimeGrid({
  columnCount = 1,
  columnHeaders,
  hourHeight = 60,
  userTz,
  renderColumn,
  showNowLine = true,
  currentDate,
}) {
  const scrollRef = useRef(null)

  // Auto-scroll to ~7 AM or current hour on mount and date navigation
  useEffect(() => {
    if (!scrollRef.current) return
    const nowInTz = utcToZonedTime(new Date(), userTz)
    const h = nowInTz.getHours()
    const scrollToHour = h >= 7 && h <= 20 ? h - 1 : 7
    scrollRef.current.scrollTop = scrollToHour * hourHeight
  }, [hourHeight, userTz, currentDate])

  const totalHeight = 24 * hourHeight
  const today = utcToZonedTime(new Date(), userTz)

  return (
    <div className="flex flex-col">
      {/* Column headers (week view) */}
      {columnHeaders && (
        <div className="flex border-b border-gray-200">
          <div className="w-14 shrink-0" />
          {columnHeaders.map((col, i) => {
            const isToday = isSameDayTz(col.date, today, userTz)
            return (
              <div
                key={i}
                className={`flex-1 min-w-[120px] text-center py-2 ${
                  i > 0 ? 'border-l border-gray-100' : ''
                }`}
              >
                <div className="text-xs text-gray-400 uppercase">{col.label}</div>
                <div
                  className={`text-sm font-semibold mt-0.5 inline-flex items-center justify-center ${
                    isToday
                      ? 'bg-primary-500 text-white rounded-full w-7 h-7'
                      : 'text-gray-900'
                  }`}
                >
                  {col.sublabel}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Scrollable grid body */}
      <div
        ref={scrollRef}
        className="overflow-y-auto overflow-x-auto"
        style={{ maxHeight: 'calc(100vh - 280px)' }}
      >
        <div className="flex" style={{ minHeight: totalHeight }}>
          {/* Hour labels gutter */}
          <div className="w-14 shrink-0 relative" style={{ height: totalHeight }}>
            {HOURS.map((h) => (
              <div
                key={h}
                className="absolute w-full text-right pr-2 text-xs text-gray-400 -mt-2"
                style={{ top: h * hourHeight }}
              >
                {h > 0 ? formatHourLabel(h) : ''}
              </div>
            ))}
          </div>

          {/* Columns */}
          <div className="flex flex-1 relative">
            {/* Horizontal gridlines */}
            {HOURS.map((h) => (
              <div
                key={`line-${h}`}
                className="absolute left-0 right-0 border-t border-gray-100"
                style={{ top: h * hourHeight }}
              />
            ))}

            {/* Now indicator */}
            {showNowLine && <NowIndicator userTz={userTz} hourHeight={hourHeight} />}

            {/* Day columns */}
            {Array.from({ length: columnCount }, (_, colIdx) => (
              <div
                key={colIdx}
                className={`flex-1 min-w-[120px] relative ${
                  colIdx > 0 ? 'border-l border-gray-100' : ''
                }`}
                style={{ height: totalHeight }}
              >
                {renderColumn(colIdx)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
